<template>
    <div class="login-form">
        <div class="login-form__fields">
            <UIInput
                :placeholder="$t('login.email')"
                :value="form.email"
                :full="true"
                :error="getEmailError"
                @changed="onChangeEmail"
            />

            <UIInputPassword
                :placeholder="$t('login.password')"
                :full="true"
                :value="form.password"
                :error="getPasswordError"
                @changed="onChangePassword"
            />

            <UIMessage v-show="message && !emailNotVerified" :message="message" />

            <div v-if="emailNotVerified" class="login-form__not-verified">
                <span class="login-form__not-verified-text">{{ $t('login.emailNotVerified') }}</span>
                <button class="login-form__verify-link" @click="onResendCode">{{ $t('login.sendVerifyCode') }}</button>
                <router-link class="login-form__verify-link" :to="getVerifyRoute">{{ $t('login.goToVerify') }}</router-link>
            </div>
        </div>

        <div class="login-form__remember">
            <UICheckbox :value="remember" @changed="onChangeRemember" />

            <p class="login-form__remember-label">{{ $t('login.remember') }}</p>
        </div>

        <UIButton
            :label="$t('login.signin')"
            view="main"
            :full="true"
            :disabled="getDisabled"
            :uppercase="true"
            @clicked="onLogin"
        />

        <UILink
            :label="$t('login.forgot')"
            view="text-main"
            to="/restore-password"
        />
    </div>
</template>

<script>
import UIInput from "@/components/ui/input/input.vue";
import UIInputPassword from "@/components/ui/input/password.vue";
import UICheckbox from "@/components/ui/checkbox/checkbox.vue";
import UIButton from "@/components/ui/button/button.vue";
import UILink from "@/components/ui/link/link.vue";
import UIMessage from "@/components/ui/message/message.vue";

import {getValidationEmail} from "@/utils/functions";
import {PASSWORD_MIN_LENGTH, RESPONSE_STATUSES} from "@/utils/constants";

import Api from "@/api/api";

const api = new Api();

export default {
    name: "LoginForm",
    components: {
        UIInput,
        UIInputPassword,
        UICheckbox,
        UIButton,
        UILink,
        UIMessage,
    },
    data() {
        return {
            form: {
                email: "",
                password: "",
            },
            remember: false,
            loading: false,
            message: "",
            emailNotVerified: false,
        };
    },
    computed: {
        getValidationEmail() {
            return getValidationEmail(this.form.email);
        },

        getEmailError() {
            if (this.message) {
                return true;
            }

            if (this.form.email && !this.getValidationEmail) {
                return !this.getValidationEmail;
            }

            return false;
        },

        getValidationPassword() {
            return this.form.password.length >= PASSWORD_MIN_LENGTH;
        },

        getPasswordError() {
            if (this.message) {
                return true;
            }

            if (this.form.password && !this.getValidationPassword) {
                return !this.getValidationPassword;
            }

            return false;
        },

        getDisabled() {
            return (
                !this.getValidationEmail ||
                !this.getValidationPassword ||
                this.loading
            );
        },

        getVerifyRoute() {
            return {
                path: "/verify-email",
                query: this.form.email ? {email: this.form.email} : {},
            };
        },
    },
    methods: {
        onChangeEmail(email) {
            this.form.email = email;
            this.emailNotVerified = false;
            this.message = "";
        },

        onChangePassword(password) {
            this.form.password = password;
            this.emailNotVerified = false;
            this.message = "";
        },

        onChangeRemember(remember) {
            this.remember = remember;
        },

        async onResendCode() {
            if (!this.form.email) return;
            try {
                await api.resendVerification(this.form.email);
            } catch (e) {
                // ignore
            }
            this.$router.push({path: "/verify-email", query: {email: this.form.email}});
        },

        async onLogin() {
            try {
                const {email, password} = this.form;

                this.loading = true;
                this.message = "";
                this.emailNotVerified = false;

                await api
                    .login({
                        login: email,
                        password,
                    })
                    .then((response) => {
                        const {data, status, message} = response;

                        if (status === RESPONSE_STATUSES.error) {
                            if (message === "user email not verified") {
                                this.emailNotVerified = true;
                            } else {
                                this.message = 'Неверный email или пароль';
                            }
                        } else {
                            this.$router.push("/");
                        }

                        this.loading = false;
                    });
            } catch (e) {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss">
.login-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    grid-row-gap: 16px;

    .login-form__fields {
        display: flex;
        flex-direction: column;
        align-items: center;
        grid-row-gap: 8px;
        width: 100%;
    }

    .login-form__remember {
        display: flex;
        align-items: center;
        grid-column-gap: 16px;
        margin-right: auto;
    }

    .login-form__remember-label {
        font-size: 14px;
        line-height: 17px;
        color: $txt-main;
    }

    .login-form__not-verified {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        grid-row-gap: 6px;
        width: 100%;
    }

    .login-form__not-verified-text {
        font-size: 13px;
        color: $status-red;
    }

    .login-form__verify-link {
        font-size: 13px;
        color: $main;
        background: none;
        border: none;
        cursor: pointer;
        padding: 0;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
}
</style>
